import React, { useEffect, useState } from "react";
import AuthenticatedImage from "../../../components/AuthenticatedImage";
import {
  Table,
  Button,
  Badge,
  Form,
  Spinner,
  Row,
  Col,
  Modal,
  Pagination,
} from "react-bootstrap";
import {
  PencilSquare,
  Lock,
  CheckCircleFill,
  XCircleFill,
  PlusCircleFill,
  EyeFill,
  Download,
  Trash,
  PeopleFill,
  Search,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddUserOffcanvas from "./AddUserOffcanvas";
import { useForm } from "react-hook-form";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Reusable styled modal header ── */
const BlueModalHeader = ({ children, onHide }) => (
  <Modal.Header
    closeButton
    className="text-white border-0"
    style={{ background: primaryGradient }}
  >
    <Modal.Title
      className="fw-bold d-flex align-items-center gap-2"
      style={{ fontSize: "0.95rem" }}
    >
      {children}
    </Modal.Title>
  </Modal.Header>
);

const statusBadge = (status) => {
  if (status === 1)
    return (
      <Badge bg="success" className="px-3 py-2 rounded-pill">
        Active
      </Badge>
    );
  if (status === 2)
    return (
      <Badge bg="danger" className="px-3 py-2 rounded-pill">
        Inactive
      </Badge>
    );
  return (
    <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill">
      Unverified
    </Badge>
  );
};

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <Tooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{ color, lineHeight: 1, opacity: disabled ? 0.4 : 1 }}
      >
        {children}
      </Button>
    </span>
  </Tooltip>
);

const Users = () => {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    reset,
    resetField,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [users, setUsers] = useState([]);
  const [viewUser, setViewUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteData, setDeleteData] = useState({ role: "", reason: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const itemsPerPage = 10;
  const token = localStorage.getItem("adminToken");

  const getLocationString = (loc) =>
    !loc ? "N/A" : typeof loc === "object" ? loc.location : loc;

  useEffect(() => {
    if (editingUser)
      reset({
        name: editingUser.name,
        number: editingUser.number,
        location: getLocationString(editingUser.location),
      });
  }, [editingUser, reset]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/getAllUsers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm,
            filterLocation,
            status: filterStatus,
          },
        },
      );
      const usersList = res.data.users || res.data.data || [];
      const total =
        res.data.total ??
        res.data.totalUsers ??
        res.data.count ??
        res.data.totalCount ??
        res.data.pagination?.total ??
        usersList.length;

      setUsers(usersList);
      setTotalUsers(total);
    } catch {
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLocations(
        (res.data.data || [])
          .filter((l) => l.status === "Active")
          .map((l) => ({ _id: l._id, location: l.location })),
      );
    } catch {
      toast.error("Failed to load locations");
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLocations();
  }, [currentPage, searchTerm, filterLocation, filterStatus]);

  const toggleStatus = async (user) => {
    if (user.user_status === 0) {
      toast.error("User email not verified.");
      return;
    }
    const newStatus = user.user_status === 1 ? 2 : 1;
    setUsers((prev) =>
      prev.map((u) =>
        u._id === user._id ? { ...u, user_status: newStatus } : u,
      ),
    );
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/editUserStatus/${user._id}`,
        { user_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`User ${newStatus === 1 ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Failed to update status");
      setUsers((prev) =>
        prev.map((u) =>
          u._id === user._id ? { ...u, user_status: user.user_status } : u,
        ),
      );
    }
  };

  const handleDelete = async (ID) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${ID}/permanent-delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { role: deleteData?.role, reason: deleteData?.reason },
      });
      toast.success("User deleted!");
      fetchUsers();
    } catch {
      toast.error("Failed to delete user");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const clearResetPasswordFields = () => {
    resetField("newPassword", { defaultValue: "" });
    resetField("confirmPassword", { defaultValue: "" });
    clearErrors(["newPassword", "confirmPassword"]);
  };

  const openResetPasswordModal = (userId) => {
    setSelectedUserId(userId);
    clearResetPasswordFields();
    setShowResetModal(true);
  };

  const closeResetPasswordModal = () => {
    setShowResetModal(false);
    setSelectedUserId("");
    clearResetPasswordFields();
  };

  const handleResetPassword = async () => {
    const newPassword = watch("newPassword"),
      confirmPassword = watch("confirmPassword");

    if (!selectedUserId) {
      toast.error("Please select a user first.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm the new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/changeUserPassword/${selectedUserId}`,
        { newPassword, confirmPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Password updated!");
      closeResetPasswordModal();
    } catch {
      toast.error("Something went wrong.");
    }
  };

  const handleDownloadPdf = async (userId) => {
    setPdfLoading(true);
    setSelectedUserId(userId);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/milestones/downloadReport/${userId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      setPdfUrl(
        URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" }),
        ),
      );
      setShowPdfModal(true);
    } catch {
      toast.error("Failed to download report");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      const formData = getValues();
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("number", formData.number || "");
      const sel = locations.find((l) => l.location === formData.location);
      fd.append("location", sel ? sel._id : formData.location || "");
      await axios.put(
        `${API_BASE_URL}/api/auth/update-user/${editingUser._id}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success("User updated!");
      fetchUsers();
      setShowEditModal(false);
      setEditingUser(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Update failed");
    }
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          User Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <PeopleFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              User Management
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {totalUsers} total users
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={() => setShowAddUser(true)}
          className="d-flex align-items-center gap-2 fw-bold"
          style={{
            background: primaryGradient,
            border: "none",
            borderRadius: "10px",
            padding: "10px 20px",
            boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
          }}
        >
          <PlusCircleFill size={16} /> Add New User
        </Button>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search name or email…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Box>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              value={filterLocation}
              onChange={(e) => {
                setFilterLocation(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Locations</option>
              {locations.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.location}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="2">Inactive</option>
              <option value="0">Unverified</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading users…
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "Name",
                  "Email",
                  "Location",
                  "Phone",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user, i) => (
                  <tr
                    key={user._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {user.name}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {user.email}
                    </td>
                    <td>{getLocationString(user.location)}</td>
                    <td>{user.number || "—"}</td>
                    <td>{statusBadge(user.user_status)}</td>
                    <td>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ActionBtn
                          tooltip={
                            user.user_status === 1 ? "Deactivate" : "Activate"
                          }
                          color={user.user_status === 1 ? "#c62828" : "#2e7d32"}
                          disabled={user.user_status === 0}
                          onClick={() => toggleStatus(user)}
                        >
                          {user.user_status === 1 ? (
                            <XCircleFill size={17} />
                          ) : (
                            <CheckCircleFill size={17} />
                          )}
                        </ActionBtn>
                        <ActionBtn
                          tooltip="Edit"
                          color="#f57f17"
                          onClick={() => {
                            setEditingUser(user);
                            setShowEditModal(true);
                          }}
                        >
                          <PencilSquare size={16} />
                        </ActionBtn>
                        <ActionBtn
                          tooltip="View"
                          color="#0d47a1"
                          onClick={() => {
                            setViewUser(user);
                            setShowViewModal(true);
                          }}
                        >
                          <EyeFill size={16} />
                        </ActionBtn>
                        <ActionBtn
                          tooltip="Reset Password"
                          color="#6a1b9a"
                          onClick={() => openResetPasswordModal(user._id)}
                        >
                          <Lock size={16} />
                        </ActionBtn>
                        <ActionBtn
                          tooltip="Download Report"
                          color="#2e7d32"
                          onClick={() => handleDownloadPdf(user._id)}
                        >
                          <Download size={16} />
                        </ActionBtn>
                        <ActionBtn
                          tooltip="Delete"
                          color="#c62828"
                          onClick={() => {
                            setItemToDelete(user._id);
                            setDeleteData({ role: user.role, reason: "" });
                            setShowDeleteModal(true);
                          }}
                        >
                          <Trash size={16} />
                        </ActionBtn>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {totalUsers > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(currentPage - 1) * itemsPerPage + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(currentPage * itemsPerPage, totalUsers)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{totalUsers}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
              />
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p =
                  totalPages <= 5
                    ? i + 1
                    : currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : currentPage - 2 + i;
                return (
                  <Pagination.Item
                    key={p}
                    active={p === currentPage}
                    onClick={() => setCurrentPage(p)}
                    style={
                      p === currentPage
                        ? {
                            "--bs-pagination-active-bg": "#1a237e",
                            "--bs-pagination-active-border-color": "#1a237e",
                          }
                        : {}
                    }
                  >
                    {p}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      <AddUserOffcanvas
        show={showAddUser}
        handleClose={() => setShowAddUser(false)}
        onUserAdded={() => {
          fetchUsers();
          toast.success("User added!");
        }}
      />

      {/* PDF Modal */}
      <Modal
        show={showPdfModal}
        onHide={() => {
          setShowPdfModal(false);
          if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl("");
          }
        }}
        size="lg"
        centered
      >
        <BlueModalHeader>📄 Milestone Report</BlueModalHeader>
        <Modal.Body style={{ background: "#f7f9ff" }}>
          {pdfLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <Spinner animation="border" style={{ color: "#0d47a1" }} />
            </Box>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              width="100%"
              height="500px"
              title="Report"
              style={{ border: "none", borderRadius: 8 }}
            />
          ) : (
            <p className="text-center text-muted py-4">No report available</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowPdfModal(false)}
          >
            Close
          </Button>
          {pdfUrl && (
            <Button
              style={{ background: primaryGradient, border: "none" }}
              onClick={() => {
                const a = document.createElement("a");
                a.href = pdfUrl;
                a.download = `report-${selectedUserId}.pdf`;
                a.click();
              }}
            >
              ⬇ Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        show={showResetModal}
        onHide={closeResetPasswordModal}
        centered
      >
        <BlueModalHeader>🔒 Reset Password</BlueModalHeader>
        <Modal.Body style={{ background: "#f7f9ff" }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>New Password</Form.Label>
              <Form.Control
                style={fieldStyle}
                type="password"
                placeholder="Enter new password"
                autoComplete="new-password"
                {...register("newPassword", { required: true })}
                isInvalid={!!errors.newPassword}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={labelStyle}>Confirm Password</Form.Label>
              <Form.Control
                style={fieldStyle}
                type="password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                {...register("confirmPassword", {
                  required: true,
                  validate: (v) =>
                    v === watch("newPassword") || "Passwords do not match",
                })}
                isInvalid={!!errors.confirmPassword}
              />
              {errors.confirmPassword && (
                <Form.Text className="text-danger">
                  {errors.confirmPassword.message}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeResetPasswordModal}
          >
            Cancel
          </Button>
          <Button
            style={{ background: primaryGradient, border: "none" }}
            onClick={handleResetPassword}
            disabled={!watch("newPassword") || !watch("confirmPassword")}
          >
            Update Password
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Profile Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
      >
        <BlueModalHeader>👤 User Profile</BlueModalHeader>
        <Modal.Body style={{ background: "#f7f9ff" }}>
          {viewUser && (
            <>
              <Box
                sx={{
                  background: primaryGradient,
                  borderRadius: "10px",
                  p: 3,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                <AuthenticatedImage
                  src={
                    viewUser.profilePicture || "https://via.placeholder.com/80"
                  }
                  alt="Profile"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid rgba(255,255,255,0.4)",
                  }}
                />
                <Typography sx={{ color: "#fff", fontWeight: 700, mt: 1 }}>
                  {viewUser.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  {viewUser.role}
                </Typography>
              </Box>
              {[
                ["Email", viewUser.email],
                ["Phone", viewUser.number || "N/A"],
                ["Location", getLocationString(viewUser.location)],
                ["Date of Birth", viewUser.dateofbirth || "N/A"],
                ["Joined", new Date(viewUser.ins_date).toLocaleDateString()],
              ].map(([k, v]) => (
                <Box
                  key={k}
                  display="flex"
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: "8px",
                    mb: 0.5,
                    background: "#fff",
                    border: "1px solid #e8ecf4",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: "#7b8ab8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: 120,
                      flexShrink: 0,
                    }}
                  >
                    {k}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.84rem",
                      color: "#1a237e",
                      fontWeight: 500,
                    }}
                  >
                    {v}
                  </Typography>
                </Box>
              ))}
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #e8ecf4",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#7b8ab8",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    width: 120,
                    flexShrink: 0,
                  }}
                >
                  Status
                </Typography>
                {statusBadge(viewUser.user_status)}
              </Box>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          reset();
        }}
        centered
      >
        <BlueModalHeader>✏️ Edit User</BlueModalHeader>
        <Modal.Body style={{ background: "#f7f9ff" }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Name *</Form.Label>
              <Form.Control
                style={fieldStyle}
                {...register("name", { required: "Name is required" })}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Phone</Form.Label>
              <Form.Control
                style={fieldStyle}
                {...register("number")}
                placeholder="Phone number"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={labelStyle}>Location</Form.Label>
              <Form.Select style={fieldStyle} {...register("location")}>
                <option value="">Select Location</option>
                {locations.map((l) => (
                  <option key={l._id} value={l.location}>
                    {l.location}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </Button>
          <Button
            style={{ background: primaryGradient, border: "none" }}
            onClick={() => handleSaveChanges(getValues())}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <BlueModalHeader>🗑 Delete User</BlueModalHeader>
        <Modal.Body style={{ background: "#f7f9ff" }}>
          <Box
            sx={{
              background: "#fff3e0",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              p: 1.5,
              mb: 2,
              fontSize: "0.84rem",
              color: "#e65100",
            }}
          >
            ⚠ This action is permanent and cannot be undone.
          </Box>
          <Form.Group>
            <Form.Label style={labelStyle}>Reason for deletion</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              style={fieldStyle}
              placeholder="Enter reason…"
              value={deleteData?.reason}
              onChange={(e) =>
                setDeleteData({ ...deleteData, reason: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(itemToDelete)}>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default Users;
